import React from "react";
import { Grid, Typography, Button, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { NearbyBranch } from "../../models/warehouse/types";
import { CheckCircleOutline } from "@mui/icons-material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { InventoryStatus } from "../../../enums/inventoryStatus";

export interface BranchCardProps {
  nearbyBranch: NearbyBranch;
  onSelect?: (warehouseCode: string) => void;
  disabled?: boolean;
}

const formatPhone = (phone?: string | null) => {
  if (!phone) return "";
  const clean = phone.replace(/\D/g, "");
  if (clean.length === 10) {
    return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
  }
  return phone;
};

const InventoryStatusIcon = (inventoryStatus: InventoryStatus | string) => {
  const status =
    typeof inventoryStatus === "string"
      ? InventoryStatus[inventoryStatus as keyof typeof InventoryStatus]
      : inventoryStatus;

  let tooltipText = "Availability Unknown";

  switch (status) {
    case InventoryStatus.Available:
      tooltipText = "Available";
      break;
    case InventoryStatus.Stocked:
      tooltipText = "Stocked";
      break;
    case InventoryStatus.SpecialItem:
      tooltipText = "Special Order";
      break;
    case InventoryStatus.ContactBranchForAvailability:
      tooltipText = "Contact Branch for Pricing";
      break;
  }
  return tooltipText;
};

const BranchCard: React.FC<BranchCardProps> = ({
  nearbyBranch,
  onSelect,
  disabled = false,
}) => {
  const phone = formatPhone(nearbyBranch.branch.contactPhoneNumber);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        flexDirection: {
          xs: "column",
          sm: "row",
        },
        alignItems: {
          xs: "start",
          sm: "center",
        },
        py: 2,
      }}
    >
      {/* LEFT: Branch Info */}
      <Grid item xs={12} md={4}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Tooltip
            title={InventoryStatusIcon(nearbyBranch.inventoryStatus)}
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  color: "black",
                  backgroundColor: "white",
                  fontSize: "13px",
                  boxShadow: 2,
                },
              },
              arrow: {
                sx: {
                  color: "#ccc",
                },
              },
            }}
            PopperProps={{
              sx: { zIndex: 9999 },
            }}
          >
            {(typeof nearbyBranch.inventoryStatus === "string"
              ? InventoryStatus[
                  nearbyBranch.inventoryStatus as keyof typeof InventoryStatus
                ]
              : nearbyBranch.inventoryStatus) ===
            InventoryStatus.SpecialItem ? (
              <HighlightOffIcon
                fontSize="medium"
                sx={{ color: "#AF272F", marginRight: "15px" }}
              />
            ) : (
              <CheckCircleOutline
                fontSize="medium"
                sx={{ color: "green", marginRight: "15px" }}
              />
            )}
          </Tooltip>
          <div>
            <Typography fontWeight="bold" sx={{ fontSize: "13px" }}>
              {nearbyBranch.branch.warehouseName}
            </Typography>
            <Typography color="primary" sx={{ fontSize: "13px" }}>
              {nearbyBranch.branch.warehouseAddress}
            </Typography>
            <Typography color="primary" sx={{ fontSize: "13px" }}>
              {nearbyBranch.branch.warehouseCity},{" "}
              {nearbyBranch.branch.warehouseState} {nearbyBranch.branch.zipCode}
            </Typography>
          </div>
        </div>
      </Grid>

      {/* CENTER: Distance & SELECT */}
      <Grid
        item
        xs={12}
        md={4}
        sx={{
          textAlign: {
            xs: "left",
            md: "right",
          },
          marginLeft: {
            xs: "50px",
            md: "auto",
          },
        }}
      >
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            display: "flex",
            justifyContent: { xs: "start", md: "end" },
            alignItems: "center",
            gap: "4px",
          }}
        >
          <LocationOnIcon fontSize="small" />
          <Typography component="span" sx={{ fontSize: "12px" }}>
            {nearbyBranch.distance
              ? `${Math.round(nearbyBranch.distance)} Miles Away`
              : "Distance Unknown"}
          </Typography>
        </Typography>
        {nearbyBranch.isSelected ? (
          <Button
            disableElevation
            color="primary"
            variant="contained"
            sx={{ mt: 1, pointerEvents: "none" }}
          >
            SELECTED
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => onSelect?.(nearbyBranch.branch.warehouseCode ?? "")}
            disabled={disabled}
            sx={{ mt: 1 }}
          >
            SELECT
          </Button>
        )}
      </Grid>

      {/* RIGHT: Hours & Contact */}
      <Grid
        item
        xs={12}
        md={4}
        sx={{
          textAlign: {
            xs: "left",
            md: "right",
          },
          marginLeft: {
            xs: "50px",
            md: "auto",
          },
        }}
      >
        <Typography variant="body2">
          <Typography component="span" sx={{ fontSize: "13px" }}>
            {nearbyBranch.workingHoursFrom} - {nearbyBranch.workingHoursTo}{" "}
          </Typography>
          {nearbyBranch.opened ? (
            <Typography
              component="span"
              color="green"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Open
            </Typography>
          ) : (
            <Typography
              component="span"
              color="red"
              fontWeight="bold"
              sx={{ fontSize: "11px" }}
            >
              Close
            </Typography>
          )}
        </Typography>
        {phone && (
          <Typography
            color="primary"
            fontWeight={500}
            sx={{ fontSize: "13px" }}
          >
            {phone}
          </Typography>
        )}
        <Typography>
          <a
            href={`mailto:${nearbyBranch.branch.contactEmail}`}
            style={{
              color: "#1976d2",
              textDecoration: "none",
              fontSize: "13px",
            }}
          >
            Email this branch
          </a>
        </Typography>
      </Grid>
    </Grid>
  );
};

export default BranchCard;
